import { useEffect, useState } from "react";
import { Button, Dialog, Portal, Text, Box, HStack, Spinner } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { searchUsers, updateUserRef } from "../../Redux/Action";
import { getLocalStorageItem } from "../../Utils/Helper";

export const AddUserUnderIBModal = ({ isOpen, onClose, ibData, refetchData }) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get whitelabel data from localStorage
  const whitelabelData = getLocalStorageItem("whitelabelData");

  // Debounce effect for search query (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || !isOpen) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    dispatch(
      searchUsers({
        payload: {
          searchQuery: debouncedQuery,
          whiteLabelID: whitelabelData?._id || null,
        },
        callback: (response) => {
          if (response?.meta?.code === 200) {
            const users = response?.data?.users || [];
            setSearchResults(users);
          } else {
            setSearchResults([]);
          }
          setIsSearching(false);
        },
      })
    );
  }, [debouncedQuery, isOpen]);

  const handleAddUser = (selectedUser) => {
    if (!ibData?._id || !ibData?.referralCode || !selectedUser?._id) {
      return;
    }

    setIsSubmitting(true);
    dispatch(
      updateUserRef({
        _id: ibData._id,
        referralCode: ibData.referralCode,
        userId: selectedUser._id,
        callback: (response) => {
          if (response?.meta?.code === 200) {
            onClose();
            refetchData();
            setSearchQuery("");
            setDebouncedQuery("");
            setSearchResults([]);
          }
          setIsSubmitting(false);
        },
      })
    );
  };

  const handleClose = () => {
    setSearchQuery("");
    setDebouncedQuery("");
    setSearchResults([]);
    onClose();
  };

  return (
    <Dialog.Root lazyMount open={isOpen}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="700px"
            maxH="80vh"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header>
              <Dialog.Title fontSize="xl" fontWeight="bold">
                Add User Under IB
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body overflowY="auto" flex={1}>
              {/* Search Input */}
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Search User (Email, Account Number, or Name)
                </Text>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search users..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3182ce";
                    e.target.style.boxShadow = "0 0 0 3px rgba(49, 130, 206, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e2e8f0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </Box>

              {/* Search Results */}
              {isSearching ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={8}
                >
                  <Spinner size="md" color="blue.500" />
                  <Text ml={3} color="gray.600" fontSize="sm">
                    Searching users...
                  </Text>
                </Box>
              ) : searchResults.length > 0 ? (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={3}>
                    Found {searchResults.length} user{searchResults.length !== 1 ? "s" : ""}
                  </Text>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {searchResults.map((user) => (
                      <Box
                        key={user._id}
                        p={3}
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="gray.200"
                        bg="gray.50"
                        _hover={{ bg: "gray.100" }}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box flex={1}>
                          <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                            {user.fname} {user.lname}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {user.email}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            Account: {user.accountno}
                          </Text>
                        </Box>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => handleAddUser(user)}
                          disabled={isSubmitting}
                        >
                          Add User+
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : searchQuery ? (
                <Box py={8} textAlign="center">
                  <Text color="gray.500" fontSize="sm">
                    No users found matching "{debouncedQuery}"
                  </Text>
                </Box>
              ) : (
                <Box py={8} textAlign="center">
                  <Text color="gray.500" fontSize="sm">
                    Start typing to search for users by email, account number, or name
                  </Text>
                </Box>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
